import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ReportMultiplier {
  id: number;
  nome: string;
  telefone?: string | null;
  email?: string | null;
  profissao?: string | null;
  rede_social?: string | null;
  observacoes?: string | null;
}

export interface ReportDemand {
  id: number;
  titulo: string;
  tipo: string;
  status: string;
  data_cadastro: string;
  data_inicio?: string | null;
  data_fim?: string | null;
  descricao?: string | null;
}

export interface CityReportData {
  cityName: string;
  uf: string;
  population: number;
  contactsCount: number;
  responsavel: string | null;
  multipliers: ReportMultiplier[];
  demands: ReportDemand[];
  dateRangeLabel: string;
  groupName: string;
  generatedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  NAO_INICIADA: 'Não Iniciada',
  EM_ANDAMENTO: 'Em Andamento',
  PAUSADA: 'Pausada',
  CANCELADA: 'Cancelada',
  CONCLUIDA: 'Concluída',
};

const TYPE_LABELS: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  CIDADE: 'Cidade',
  ENTIDADE: 'Entidade',
  GRUPO: 'Grupo',
};

const STATUS_COLORS: Record<string, string> = {
  NAO_INICIADA: '#64748b',
  EM_ANDAMENTO: '#2563eb',
  PAUSADA: '#d97706',
  CANCELADA: '#dc2626',
  CONCLUIDA: '#16a34a',
};

function fmtDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

function fmtNumber(n: number): string {
  return n.toLocaleString('pt-BR');
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = '#2563eb';
const LIGHT_BG = '#f8fafc';
const BORDER = '#e2e8f0';
const TEXT_DARK = '#1e293b';
const TEXT_MID = '#475569';
const TEXT_LIGHT = '#94a3b8';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: TEXT_DARK,
    backgroundColor: '#ffffff',
    paddingTop: 40,
    paddingBottom: 50,
    paddingHorizontal: 40,
  },

  // ── Header ──
  header: {
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 2,
    borderBottomColor: ACCENT,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  headerLeft: {
    flex: 1,
  },
  headerGroupName: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: ACCENT,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    letterSpacing: -0.3,
  },
  headerSubtitle: {
    fontSize: 10,
    color: TEXT_MID,
    marginTop: 2,
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  headerDate: {
    fontSize: 9,
    color: TEXT_LIGHT,
  },

  // ── Overview ──
  overviewCard: {
    backgroundColor: LIGHT_BG,
    borderRadius: 6,
    padding: 16,
    marginBottom: 20,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  overviewItem: {
    width: '50%',
    paddingRight: 12,
    marginBottom: 10,
  },
  overviewItemFull: {
    width: '100%',
    marginBottom: 0,
  },
  overviewLabel: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_LIGHT,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 3,
  },
  overviewValue: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
  },
  overviewValueSmall: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
  },

  // ── Section ──
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    marginTop: 6,
  },
  sectionBar: {
    width: 3,
    height: 14,
    backgroundColor: ACCENT,
    borderRadius: 2,
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    letterSpacing: 0.2,
  },
  sectionSubtitle: {
    fontSize: 8.5,
    color: TEXT_MID,
    marginLeft: 'auto',
  },
  sectionContainer: {
    marginBottom: 20,
  },

  // ── Table ──
  table: {
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: '#ffffff',
  },
  tableRowAlt: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: LIGHT_BG,
  },
  tableRowLast: {
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: '#ffffff',
  },
  tableRowLastAlt: {
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: LIGHT_BG,
  },
  thCell: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_LIGHT,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  tdCell: {
    fontSize: 9,
    color: TEXT_DARK,
  },
  tdCellMuted: {
    fontSize: 9,
    color: TEXT_MID,
  },

  // ── Status badge ──
  statusBadge: {
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    alignSelf: 'flex-start',
  },
  statusText: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
  },

  // ── Empty state ──
  emptyBox: {
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 6,
    padding: 16,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 9,
    color: TEXT_LIGHT,
    fontStyle: 'italic',
  },

  // ── Footer ──
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: BORDER,
    paddingTop: 8,
  },
  footerText: {
    fontSize: 8,
    color: TEXT_LIGHT,
  },
});

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <View style={styles.sectionHeader}>
    <View style={styles.sectionBar} />
    <Text style={styles.sectionTitle}>{title}</Text>
    {subtitle && <Text style={styles.sectionSubtitle}>{subtitle}</Text>}
  </View>
);

// Column width configs
const MULT_COLS = {
  nome: '28%',
  telefone: '20%',
  email: '22%',
  profissao: '18%',
  rede: '12%',
};

const DEMAND_COLS = {
  titulo: '40%',
  tipo: '14%',
  status: '22%',
  data: '24%',
};

const MultiplicadoresTable: React.FC<{ data: ReportMultiplier[] }> = ({ data }) => {
  if (data.length === 0) {
    return (
      <View style={styles.emptyBox}>
        <Text style={styles.emptyText}>Nenhum multiplicador cadastrado para esta cidade.</Text>
      </View>
    );
  }

  return (
    <View style={styles.table}>
      {/* Header */}
      <View style={styles.tableHeaderRow}>
        <Text style={[styles.thCell, { width: MULT_COLS.nome }]}>Nome</Text>
        <Text style={[styles.thCell, { width: MULT_COLS.telefone }]}>Telefone</Text>
        <Text style={[styles.thCell, { width: MULT_COLS.email }]}>E-mail</Text>
        <Text style={[styles.thCell, { width: MULT_COLS.profissao }]}>Profissão</Text>
        <Text style={[styles.thCell, { width: MULT_COLS.rede }]}>Rede Social</Text>
      </View>
      {/* Rows */}
      {data.map((m, i) => {
        const isLast = i === data.length - 1;
        const isAlt = i % 2 !== 0;
        const rowStyle = isLast
          ? isAlt ? styles.tableRowLastAlt : styles.tableRowLast
          : isAlt ? styles.tableRowAlt : styles.tableRow;
        return (
          <View style={rowStyle} key={m.id}>
            <Text style={[styles.tdCell, { width: MULT_COLS.nome, fontFamily: 'Helvetica-Bold' }]}>
              {m.nome}
            </Text>
            <Text style={[styles.tdCellMuted, { width: MULT_COLS.telefone }]}>
              {m.telefone || '—'}
            </Text>
            <Text style={[styles.tdCellMuted, { width: MULT_COLS.email }]}>
              {m.email || '—'}
            </Text>
            <Text style={[styles.tdCellMuted, { width: MULT_COLS.profissao }]}>
              {m.profissao || '—'}
            </Text>
            <Text style={[styles.tdCellMuted, { width: MULT_COLS.rede }]}>
              {m.rede_social || '—'}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

const DemandsTable: React.FC<{ data: ReportDemand[]; dateRangeLabel: string }> = ({
  data,
  dateRangeLabel,
}) => {
  if (data.length === 0) {
    return (
      <View style={styles.emptyBox}>
        <Text style={styles.emptyText}>
          Nenhuma demanda encontrada no período {dateRangeLabel}.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.table}>
      {/* Header */}
      <View style={styles.tableHeaderRow}>
        <Text style={[styles.thCell, { width: DEMAND_COLS.titulo }]}>Título</Text>
        <Text style={[styles.thCell, { width: DEMAND_COLS.tipo }]}>Tipo</Text>
        <Text style={[styles.thCell, { width: DEMAND_COLS.status }]}>Status</Text>
        <Text style={[styles.thCell, { width: DEMAND_COLS.data }]}>Data de Cadastro</Text>
      </View>
      {/* Rows */}
      {data.map((d, i) => {
        const isLast = i === data.length - 1;
        const isAlt = i % 2 !== 0;
        const rowStyle = isLast
          ? isAlt ? styles.tableRowLastAlt : styles.tableRowLast
          : isAlt ? styles.tableRowAlt : styles.tableRow;
        const statusColor = STATUS_COLORS[d.status] || TEXT_MID;
        const statusBg = statusColor + '18'; // 18 = ~10% opacity hex
        return (
          <View style={rowStyle} key={d.id}>
            <Text style={[styles.tdCell, { width: DEMAND_COLS.titulo, fontFamily: 'Helvetica-Bold' }]}>
              {d.titulo}
            </Text>
            <Text style={[styles.tdCellMuted, { width: DEMAND_COLS.tipo }]}>
              {TYPE_LABELS[d.tipo] || d.tipo}
            </Text>
            <View style={{ width: DEMAND_COLS.status, justifyContent: 'center' }}>
              <View style={[styles.statusBadge, { backgroundColor: statusBg }]}>
                <Text style={[styles.statusText, { color: statusColor }]}>
                  {STATUS_LABELS[d.status] || d.status}
                </Text>
              </View>
            </View>
            <Text style={[styles.tdCellMuted, { width: DEMAND_COLS.data }]}>
              {fmtDate(d.data_cadastro)}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

// ─── Main Document ─────────────────────────────────────────────────────────────

const CityReportDocument: React.FC<{ data: CityReportData }> = ({ data }) => {
  const {
    cityName,
    uf,
    population,
    contactsCount,
    responsavel,
    multipliers,
    demands,
    dateRangeLabel,
    groupName,
    generatedAt,
  } = data;

  return (
    <Document
      title={`Relatório — ${cityName}/${uf}`}
      author={groupName}
      subject="Relatório de Cidade"
    >
      <Page size="A4" style={styles.page}>
        {/* ── Header ── */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.headerGroupName}>{groupName}</Text>
            <Text style={styles.headerTitle}>
              {cityName} – {uf}
            </Text>
            <Text style={styles.headerSubtitle}>Relatório de Cidade</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.headerDate}>Gerado em {generatedAt}</Text>
          </View>
        </View>

        {/* ── Overview ── */}
        <View style={styles.overviewCard}>
          <View style={styles.overviewItem}>
            <Text style={styles.overviewLabel}>Município</Text>
            <Text style={styles.overviewValue}>
              {cityName} – {uf}
            </Text>
          </View>
          <View style={styles.overviewItem}>
            <Text style={styles.overviewLabel}>Responsável</Text>
            <Text style={styles.overviewValueSmall}>
              {responsavel || '—'}
            </Text>
          </View>
          <View style={styles.overviewItem}>
            <Text style={styles.overviewLabel}>População</Text>
            <Text style={styles.overviewValue}>
              {population > 0 ? `${fmtNumber(population)} hab.` : '—'}
            </Text>
          </View>
          <View style={styles.overviewItem}>
            <Text style={styles.overviewLabel}>Contatos Registrados</Text>
            <Text style={styles.overviewValue}>{fmtNumber(contactsCount)}</Text>
          </View>
        </View>

        {/* ── Multiplicadores ── */}
        <View style={styles.sectionContainer}>
          <SectionHeader
            title="Multiplicadores"
            subtitle={`${multipliers.length} encontrado(s)`}
          />
          <MultiplicadoresTable data={multipliers} />
        </View>

        {/* ── Demandas ── */}
        <View style={styles.sectionContainer}>
          <SectionHeader
            title="Demandas"
            subtitle={`${demands.length} no período ${dateRangeLabel}`}
          />
          <DemandsTable data={demands} dateRangeLabel={dateRangeLabel} />
        </View>

        {/* ── Footer ── */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{groupName} — {cityName}/{uf}</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default CityReportDocument;
